<template>
  <section class="property-gallery mt-4">
    <Skeleton v-if="!isImageLoaded && hasCoverImage"/>
    <picture v-show="hasCoverImage">
      <source media="(max-width: 430px)" :srcset="coverImage.mobile" />
      <img
          ref="coverImg"
          :src="coverImage.full"
          :alt="property.name"
          @load="onImageLoad"
          @error="isImageLoaded = false"
      >
    </picture>
    <PhotoPlaceholder v-show="!hasCoverImage"/>
    <div
        v-if="imagesQty > 1"
        @click="isModalVisible = true"
        class="gallery-preview__total text-caption">
      <span class="icon icon-image"></span>
      +{{imagesQty - 1}} {{$t('photos')}}
    </div>

    <DialogCloseOutside
        v-model="isModalVisible"
        :showHeader="false"
        position="right"
        :block-scroll="true"
        :modal="true"
        :draggable="false"
        class="gallery-modal-container room-modal-container custom-scrollbar"
    >
      <div class="pb-4 gallery-modal-header">
          <button class="mobile-only" aria-label="Close gallery" @click="isModalVisible = false">
            <span class="icon icon-chevron-left color-blue text-title-1"></span>
          </button>
          <span class="flex-1 text-title-1 fw-bold room-info-dialog-header">{{$t('Photos')}}</span>
          <button class="desktop-only" aria-label="Open gallery" @click="isModalVisible = false">
            <span class="icon icon-close color-blue text-title-1"></span>
          </button>
        </div>
      <div class="gallery-photos-wrapper">
          <ActivityPropertyPageGallerySection
              :photos="images"
              :header="''"
          />
        </div>
    </DialogCloseOutside>
  </section>
</template>

<script setup lang="ts">
import {useActivityPropertyStore} from '~/store/activityProperty'
import ActivityPropertyPageGallerySection
  from "~/components/ActivityPropertyPage/ActivityPropertyPageGallerySection.vue";
import { imageUrlWithProxy } from '~/utils/imageproxy'
import PhotoPlaceholder from "~/components/PhotoPlaceholder.vue";
import DialogCloseOutside from "~/components/DialogCloseOutside.vue";

const property = useActivityPropertyStore().property
let isImageLoaded = ref(false as boolean)
let isModalVisible = ref(false as boolean)
let selectedCategory = ref('All')

//computed
const images = computed(() => property.images)
const imagesQty = computed(() => countImgQty(property.images))

const hasCoverImage = ref(!!property.cover_image_url)


const coverImage = computed(() => {
  return {
    full: imageUrlWithProxy(property.cover_image_url, 1120, 430),
    mobile: imageUrlWithProxy(property.cover_image_url, 430, 298)
  }
})


function countImgQty(images: any) {
  return images.length;
}
function onImageLoad() {
  isImageLoaded.value = true
  hasCoverImage.value = true
}

// SSR delivers the <img> in the HTML, so it often completes before Vue hydrates and
// binds @load — the event never fires and the skeleton never clears. Check on mount.
const coverImg = ref<HTMLImageElement | null>(null)
onMounted(() => {
  if (coverImg.value?.complete && coverImg.value.naturalWidth > 0) onImageLoad()
})

</script>

<style lang="scss" scoped>
.property-gallery {
  height: 430px;
  position: relative;

  //vertical tablet
  @media screen and (max-width: 768px) and (min-width: 431px){
    height: 280px;
  }

  @media screen and (max-width: 430px) {
    max-height: 298px;
  }

  img {
    width: 100%;
    max-width: 100%;
    height: 430px;
    object-fit: cover;
    object-position: center;
    border-radius: 8px;
    position: relative;
    z-index: 1;

    @media screen and (max-width: 768px) and (min-width: 431px){
      height: 280px;
    }

    @media screen and (max-width: 430px) {
      max-width: inherit;
      left: -16px;
      width: 100vw;
      max-height: 298px;
      border-radius: 0;
    }
  }
}

.gallery-modal-header {
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    gap: 24px;
  }

  button {
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
  }
}

.gallery-modal-filters {
  gap: 8px;
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 4px;

  .category-button {
    height: 32px;
  }
}

.gallery-photos-wrapper {
  @media (max-width: 768px) {
    max-height: 100vh;
    padding-right: 10px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--white);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--grey);
    border-radius: 10px;
  }
}

.gallery-preview__total {
  position: absolute;
  right: 12px;
  top: 12px;
  padding: 6px 12px;
  border-radius: 20px;
  background-color: var(--black-transparent);
  color: var(--white);
  z-index: 1;
  cursor: pointer;
  font-weight: 500;

  display: flex;
  align-items: center;
  gap: 4px;

  .icon {
    font-size: 14px;
  }

  &:hover {
    background-color: var(--black);
  }

  @media (max-width: 430px) {
    right: 0;
  }
}

/* Same rule, same selector, as the hotel gallery: both sections are .property-gallery,
   so whichever component's stylesheet loads last wins globally. Sizing off the gallery
   rather than a hard-coded height keeps the two in agreement whatever their heights. */
:global(.property-gallery .p-skeleton) {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
  height: 100% !important;
  border-radius: 8px !important;
}
</style>
